import React, { useContext, useEffect, useMemo, useState } from 'react';
import userPermissionContext from '../../../context/userPermissionContext';
import { KTSVG } from '../../../_metronic/helpers';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const DASHBOARD_CACHE_TTL_MS = 2 * 60 * 1000;
const toNum = (v, d = 0) => { const n = Number(v); return Number.isFinite(n) ? n : d; };
const extractArray = (payload, key) => {
	if (!payload) return [];
	if (Array.isArray(payload)) return payload;
	if (Array.isArray(payload?.data)) return payload.data;
	if (key && Array.isArray(payload?.[key])) return payload[key];
	if (key && Array.isArray(payload?.data?.[key])) return payload.data[key];
	const first = Object.values(payload || {}).find(v => Array.isArray(v));
	if (first) return first;
	if (payload?.data && typeof payload.data === 'object') {
		const nested = Object.values(payload.data).find(v => Array.isArray(v));
		if (nested) return nested;
	}
	return [];
};

const VEHICLE_TYPE_META = {
	lightVehicle: { title: 'Light Vehicle', icon: '🚙', color: '#0369a1', bg: '#f0f9ff' },
	motorCycle: { title: 'Motor Cycle', icon: '🏍️', color: '#b45309', bg: '#fffbeb' },
};

const TYPE_RENEWAL_KEYS = {
	lightVehicle: [
		{ label: 'Tax Token', field: 'lvTaxTokenCount' },
		{ label: 'Fitness', field: 'lvFitnessCount' },
		{ label: 'Route Permit', field: 'lvRoutePermitCount' },
	],
	motorCycle: [
		{ label: 'Tax Token', field: 'mcTaxTokenCount' },
	],
};

const TYPE_MISSING_DOC_KEYS = {
	lightVehicle: [
		{ label: 'Missing Tax Token Date', field: 'noTaxToken' },
		{ label: 'Missing Fitness Date', field: 'noFitness' },
		{ label: 'Missing Route Permit Date', field: 'noRoutePermit' },
	],
	motorCycle: [
		{ label: 'Missing Tax Token Date', field: 'noTaxToken' },
	],
};

const getTypeStats = (extStats, type) => extStats?.byType?.[type] || {};

const buildDashboardCacheKey = (projectId, expenseMonth, expenseYear) =>
	`vms_dashboard_${projectId || 'all'}_${expenseMonth || 'all'}_${expenseYear || 'all'}`;

const readDashboardCache = (cacheKey) => {
	try {
		const raw = sessionStorage.getItem(cacheKey);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (!parsed?.savedAt || Date.now() - parsed.savedAt > DASHBOARD_CACHE_TTL_MS) return null;
		return parsed.data || null;
	} catch {
		return null;
	}
};

const writeDashboardCache = (cacheKey, data) => {
	try {
		sessionStorage.setItem(cacheKey, JSON.stringify({ savedAt: Date.now(), data }));
	} catch {
		// Ignore storage quota / private mode errors.
	}
};

const applyDashboardData = (d, setters) => {
	setters.setFleet(d.fleet || {});
	setters.setExpiryExpired(d.expiryExpired || {});
	setters.setExpirySoon(d.expirySoon || {});
	setters.setExpenseSummary(d.expenseSummary || {});
	setters.setProjectVehicle(extractArray(d, 'projectVehicle'));
	setters.setCategoryVehicle(extractArray(d, 'categoryVehicle'));
	setters.setExtStats(d.extStats || {});
	setters.setLastSync(new Date());
};

/* ── UI Core Components ── */
const StatCard = React.memo(({ icon, label, value, color = '#1e293b', bg = '#fff', borderColor = '#e2e8f0', sub }) => (
	<div className="stat-card" style={{ border: `1px solid ${borderColor}`, background: bg }}>
		<div className="stat-card-header">
			{icon && <span style={{ fontSize: 18 }}>{icon}</span>}
			<span className="stat-card-label">{label}</span>
		</div>
		<div className="stat-card-value" style={{ color }}>{value}</div>
		{sub && <div className="stat-card-sub">{sub}</div>}
	</div>
));

const MiniStat = React.memo(({ label, value, color = '#1e293b', bg = '#f8fafc' }) => (
	<div className="mini-stat" style={{ background: bg }}>
		<div className="mini-stat-value" style={{ color }}>{value}</div>
		<div className="mini-stat-label" style={{ color }}>{label}</div>
	</div>
));

const BarRow = React.memo(({ label, value, max, color, showPercent = false }) => {
	const pct = max > 0 ? ((value / max) * 100) : 0;
	return (
		<div className="bar-row">
			<div className="bar-row-header">
				<span className="bar-row-label">{label}</span>
				<span className="bar-row-value">{value}{showPercent ? ` (${pct.toFixed(0)}%)` : ''}</span>
			</div>
			<div className="bar-track">
				<div className="bar-fill" style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
			</div>
		</div>
	);
});

const SectionTitle = React.memo(({ icon, title }) => (
	<div className="section-title">
		<span style={{ fontSize: 16 }}>{icon}</span> {title}
	</div>
));

const PanelCard = React.memo(({ title, children, linkHref, linkLabel }) => (
	<div className="panel-card">
		<div className="panel-card-header">
			<span className="panel-card-title">{title}</span>
			{linkHref && <a href={`#${linkHref}`} className="panel-card-link">{linkLabel || 'View →'}</a>}
		</div>
		<div className="panel-card-body">{children}</div>
	</div>
));

const AlertRow = React.memo(({ label, count }) => (
	<div className="alert-row">
		<span className="alert-row-label">{label}</span>
		{count > 0
			? <span className="alert-badge alert-badge-danger">{count}</span>
			: <span className="alert-check">✓</span>
		}
	</div>
));

/* ── Extracted Feature Components ── */
const FleetOverview = React.memo(({ fleet, loading }) => {
	const V = v => loading ? <span style={{ color: '#cbd5e1' }}>—</span> : v;
	return (
		<>
			<SectionTitle icon="🚗" title="Fleet Overview" />
			<div className='row g-3 mb-4'>
				<div className='col-6 col-md-4 col-xl'>
					<StatCard icon="📊" label="Total Active" value={V(toNum(fleet.totalActiveVehicles))} color="#1e293b" />
				</div>
				<div className='col-6 col-md-4 col-xl'>
					<StatCard icon="🏢" label="In-House" value={V(toNum(fleet.inHouseVehicleCount ?? fleet.inHouseVehcileCount))} color="#1e40af" borderColor="#bfdbfe" bg="#f0f7ff" />
				</div>
				<div className='col-6 col-md-4 col-xl'>
					<StatCard icon="🤝" label="Third-Party" value={V(toNum(fleet.thirdPartyVehicleCount))} color="#6d28d9" borderColor="#ddd6fe" bg="#f5f3ff" />
				</div>
			</div>

			<SectionTitle icon="🏷️" title="Vehicle Type Breakdown" />
			<div className='row g-3 mb-4'>
				<div className='col-lg-6'>
					<PanelCard title="🚙 Light Vehicle">
						<div className="panel-card-padding" style={{ display: 'flex', gap: 12 }}>
							<MiniStat label="Active" value={V(toNum(fleet.activeLightVehicle))} color="#0369a1" bg="#f0f9ff" />
							<MiniStat label="In-House" value={V(toNum(fleet.inHouseLightVehicle))} color="#1e40af" bg="#eff6ff" />
							<MiniStat label="Third-Party" value={V(toNum(fleet.thirdPartyLightVehicle))} color="#6d28d9" bg="#f5f3ff" />
						</div>
					</PanelCard>
				</div>
				<div className='col-lg-6'>
					<PanelCard title="🏍️ Motor Cycle">
						<div className="panel-card-padding" style={{ display: 'flex', gap: 12 }}>
							<MiniStat label="Active" value={V(toNum(fleet.activeMotorCycle))} color="#b45309" bg="#fffbeb" />
							<MiniStat label="In-House" value={V(toNum(fleet.inHouseMotorCycle))} color="#92400e" bg="#fef3c7" />
							<MiniStat label="Third-Party" value={V(toNum(fleet.thirdPartyMotorCycle))} color="#c2410c" bg="#fff7ed" />
						</div>
					</PanelCard>
				</div>
			</div>
		</>
	);
});

const OperationsDriver = React.memo(({ extStats, loading }) => {
	const V = v => loading ? <span style={{ color: '#cbd5e1' }}>—</span> : v;
	const renderTypeColumn = (typeKey) => {
		const typeStats = getTypeStats(extStats, typeKey);
		const meta = VEHICLE_TYPE_META[typeKey];
		const functionalStatus = typeStats?.functionalStatus || extStats?.functionalStatus || {};
		const driverStats = typeStats?.driverStats || extStats?.driverStats || {};
		const selfDriven = typeStats?.selfDriven || extStats?.selfDriven || {};
		const carUseType = typeStats?.carUseType || extStats?.carUseType || {};
		const rentalType = typeStats?.rentalType || extStats?.rentalType || {};
		const running = toNum(functionalStatus?.running);
		const runningReserved = toNum(functionalStatus?.runningReserved);
		const breakdown = toNum(functionalStatus?.breakdown);
		const total = running + runningReserved + breakdown || 1;

		return (
			<div className='col-12 col-xl-6'>
				<div className='panel-card' style={{ borderTop: `3px solid ${meta.color}`, background: meta.bg }}>
					<div className='panel-card-header'>
						<span className='panel-card-title'>{meta.icon} {meta.title}</span>
					</div>
					<div className='panel-card-body'>
						<div className='row g-3'>
							<div className='col-md-6'>
								<PanelCard title="🔧 Functional Status">
									<div className="panel-card-padding">
										<BarRow label="Running" value={running} max={total} color="#16a34a" showPercent />
										<BarRow label="Reserved" value={runningReserved} max={total} color="#f59e0b" showPercent />
										<BarRow label="Breakdown" value={breakdown} max={total} color="#dc2626" showPercent />
									</div>
								</PanelCard>
							</div>
							<div className='col-md-6'>
								<PanelCard title="👤 Driver Overview">
									<div className="panel-card-padding">
										<div style={{ textAlign: 'center', marginBottom: 10 }}>
											<div style={{ fontSize: 30, fontWeight: 700, color: '#1e293b' }}>{V(toNum(driverStats?.totalActive))}</div>
											<div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600 }}>Total Active Drivers</div>
										</div>
										<div style={{ display: 'flex', gap: 8 }}>
											<MiniStat label="Assigned" value={V(toNum(driverStats?.assigned))} color="#166534" bg="#f0fdf4" />
											<MiniStat label="Unassigned" value={V(toNum(driverStats?.unassigned))} color="#dc2626" bg="#fef2f2" />
										</div>
										<div style={{ marginTop: 10, borderTop: '1px solid #f1f5f9', paddingTop: 8 }}>
											<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
												<span style={{ color: '#475569' }}>🚘 Self-Driven</span>
												<span style={{ fontWeight: 700, color: '#0369a1' }}>{V(toNum(selfDriven?.selfDriven))}</span>
											</div>
											<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
												<span style={{ color: '#475569' }}>👨‍✈️ Driver-Assigned</span>
												<span style={{ fontWeight: 700, color: '#6d28d9' }}>{V(toNum(selfDriven?.driverAssigned))}</span>
											</div>
										</div>
									</div>
								</PanelCard>
							</div>
							<div className='col-md-6'>
								<PanelCard title="🔄 Usage Type">
									<div className="panel-card-padding" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
										<MiniStat label="Pool" value={V(toNum(carUseType?.pool))} color="#0ea5e9" bg="#f0f9ff" />
										<MiniStat label="Individual" value={V(toNum(carUseType?.individual))} color="#8b5cf6" bg="#f5f3ff" />
										<MiniStat label="Sharing" value={V(toNum(carUseType?.sharing))} color="#f59e0b" bg="#fffbeb" />
									</div>
								</PanelCard>
							</div>
							<div className='col-md-6'>
								<PanelCard title="📋 Rental Type">
									<div className="panel-card-padding" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
										<MiniStat label="Rental (Full)" value={V(toNum(rentalType?.rentalCarFull))} color="#16a34a" bg="#f0fdf4" />
										<MiniStat label="Rental (Body)" value={V(toNum(rentalType?.rentalCarBody))} color="#0369a1" bg="#f0f9ff" />
										<MiniStat label="Not Applicable" value={V(toNum(rentalType?.notApplicable))} color="#64748b" bg="#f8fafc" />
									</div>
								</PanelCard>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	};

	return (
		<>
			<SectionTitle icon="⚙️" title="Operations & Driver" />
			<div className='row g-3 mb-4'>
				{renderTypeColumn('lightVehicle')}
				{renderTypeColumn('motorCycle')}
			</div>
		</>
	);
});

const AlertsCompliance = React.memo(({ extStats, expiryExpired, expirySoon, loading }) => {
	const V = v => loading ? <span style={{ color: '#cbd5e1' }}>—</span> : v;
	const lightDocTotals = useMemo(() => {
		const keys = TYPE_RENEWAL_KEYS.lightVehicle;
		const expired = keys.reduce((s, k) => s + toNum(expiryExpired?.[k.field]), 0);
		const soon = keys.reduce((s, k) => s + toNum(expirySoon?.[k.field]), 0);
		return { expired, soon, total: expired + soon, keys };
	}, [expiryExpired, expirySoon]);
	const motorDocTotals = useMemo(() => {
		const keys = TYPE_RENEWAL_KEYS.motorCycle;
		const expired = keys.reduce((s, k) => s + toNum(expiryExpired?.[k.field]), 0);
		const soon = keys.reduce((s, k) => s + toNum(expirySoon?.[k.field]), 0);
		return { expired, soon, total: expired + soon, keys };
	}, [expiryExpired, expirySoon]);
	const typeTotals = {
		lightVehicle: lightDocTotals,
		motorCycle: motorDocTotals,
	};

	const renderTypeBlock = (type) => {
		const typeStats = getTypeStats(extStats, type);
		const totals = typeTotals[type];
		const meta = VEHICLE_TYPE_META[type];

		return (
			<div className='col-12 col-xl-6'>
				<div className='panel-card' style={{ borderTop: `3px solid ${meta.color}`, background: meta.bg }}>
					<div className='panel-card-header'>
						<span className='panel-card-title'>{meta.icon} {meta.title}</span>
					</div>
					<div className='panel-card-body'>
						<div className='row g-3'>
							<div className='col-md-6'>
								<PanelCard title="🚨 Vehicle Alerts">
									<div>
										<AlertRow label="No Driver Assigned" count={toNum(typeStats?.unassigned?.noDriver)} />
										<AlertRow label="No Project Assigned" count={toNum(typeStats?.unassigned?.noProject)} />
										{(TYPE_MISSING_DOC_KEYS[type] || []).map(({ label, field }) => (
											<AlertRow key={`${type}-${field}`} label={label} count={toNum(typeStats?.missingDocs?.[field])} />
										))}
									</div>
								</PanelCard>
							</div>
							<div className='col-md-6'>
								<PanelCard title="📄 Document Renewals" linkHref="/to-do-list" linkLabel="To-do list →">
									<table className='table table-sm mb-0' style={{ fontSize: 12 }}>
										<thead><tr className="table-header">
											<th style={{ paddingLeft: 14 }}>Document</th>
											<th className='text-center' style={{ color: '#dc2626' }}>Expired</th>
											<th className='text-center' style={{ color: '#d97706' }}>Due 30d</th>
										</tr></thead>
										<tbody>
											{totals.keys.map(({ label, field }) => {
												const exp = toNum(expiryExpired?.[field]);
												const soon = toNum(expirySoon?.[field]);
												return (
													<tr key={`${type}-${field}`}>
														<td style={{ paddingLeft: 14 }}>{label}</td>
														<td className='text-center'>{exp > 0 ? <span className="alert-badge alert-badge-danger">{exp}</span> : '0'}</td>
														<td className='text-center'>{soon > 0 ? <span className="alert-badge alert-badge-warning">{soon}</span> : '0'}</td>
													</tr>
												);
											})}
										</tbody>
									</table>
									<div className='mt-2' style={{ display: 'flex', gap: 8 }}>
										<div style={{ flex: 1, background: totals.expired > 0 ? '#fef2f2' : '#f0fdf4', borderRadius: 8, padding: '10px', textAlign: 'center', border: `1px solid ${totals.expired > 0 ? '#fecaca' : '#bbf7d0'}` }}>
											<div style={{ fontSize: 18, fontWeight: 700, color: totals.expired > 0 ? '#dc2626' : '#16a34a' }}>{V(totals.expired)}</div>
											<div style={{ fontSize: 10, fontWeight: 600, color: '#64748b' }}>Expired</div>
										</div>
										<div style={{ flex: 1, background: totals.soon > 0 ? '#fffbeb' : '#f0fdf4', borderRadius: 8, padding: '10px', textAlign: 'center', border: `1px solid ${totals.soon > 0 ? '#fde68a' : '#bbf7d0'}` }}>
											<div style={{ fontSize: 18, fontWeight: 700, color: totals.soon > 0 ? '#d97706' : '#16a34a' }}>{V(totals.soon)}</div>
											<div style={{ fontSize: 10, fontWeight: 600, color: '#64748b' }}>Due in 30 Days</div>
										</div>
									</div>
									<div style={{ textAlign: 'center', fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
										Total documents needing attention: <span style={{ fontWeight: 700, color: '#1e293b' }}>{totals.total}</span>
									</div>
								</PanelCard>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	};

	return (
		<>
			<SectionTitle icon="⚠️" title="Alerts & Compliance" />
			<div className='row g-3 mb-4'>
				{renderTypeBlock('lightVehicle')}
				{renderTypeBlock('motorCycle')}
			</div>
		</>
	);
});

const ExpenseOverview = React.memo(({ expenseSummary, extStats, activeLV, activeMC, loading, month, year, onMonthChange, onYearChange }) => {
	const V = v => loading ? <span style={{ color: '#cbd5e1' }}>—</span> : v;

	const currentYear = new Date().getFullYear();
	const years = Array.from({ length: 10 }, (_, i) => currentYear - i);

	const renderColumn = (title, icon, type, activeCount) => {
		const expSum = expenseSummary?.[type] || {};
		const hc = expSum.expenseHeadWiseTotalCost || [];
		const topExp = extStats?.topExpenseVehicles?.[type] || [];
		const expTotal = toNum(expSum.totalCost);
		const expVariance = toNum(expSum.costVariancePercentage);
		const costPerVehicle = activeCount > 0 ? Math.round(expTotal / activeCount) : 0;

		return (
			<div className='col-12 col-xl-6'>
				<div style={{ fontWeight: 600, fontSize: 16, marginBottom: 12, borderBottom: '2px solid #e2e8f0', paddingBottom: 6 }}>
					<span style={{ marginRight: 6 }}>{icon}</span>{title}
				</div>
				<div className='row g-3 mb-3'>
					<div className='col-6'>
						<StatCard icon="💵" label="Total Expense" value={V(`৳${expTotal.toLocaleString()}`)}
							color="#1e293b" sub={!loading && expVariance !== 0 ? `${expVariance > 0 ? '↑' : '↓'} ${Math.abs(expVariance)}% vs prior` : undefined} />
					</div>
					<div className='col-6'>
						<StatCard icon="📈" label="Avg Cost / Vehicle" value={V(`৳${costPerVehicle.toLocaleString()}`)}
							color="#1e293b" sub={`Based on ${activeCount} active`} />
					</div>
				</div>
				<div>
					<PanelCard title="🏆 Top 5 Expense Vehicles">
					<div style={{ maxHeight: 180, overflowY: 'auto' }}>
						<table className='table table-sm mb-0' style={{ fontSize: 12 }}>
							<tbody>
								{topExp.map((v, i) => {
									const topMax = toNum(topExp[0]?.totalExpense) || 1;
									const exp = toNum(v?.totalExpense);
									return (
										<tr key={i}>
											<td style={{ paddingLeft: 14, width: 24, fontWeight: 700, color: i === 0 ? '#f59e0b' : '#94a3b8' }}>{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`}</td>
											<td style={{ fontWeight: 600 }}>{v?.vehicleNumber || '-'}</td>
											<td className='text-end' style={{ fontWeight: 700, color: '#1e293b' }}>৳{exp.toLocaleString()}</td>
											<td style={{ width: 60 }}>
												<div className="bar-track" style={{ height: 5 }}>
													<div className="bar-fill" style={{ width: `${(exp / topMax) * 100}%`, background: '#dc2626' }} />
												</div>
											</td>
										</tr>
									);
								})}
								{!loading && topExp.length === 0 && <tr><td colSpan={4} className='text-muted text-center py-2'>No data</td></tr>}
							</tbody>
						</table>
					</div>
					</PanelCard>
				</div>
				<div className="mt-3">
					<PanelCard title="💳 Expense Head Wise Cost">
						<div style={{ maxHeight: 250, overflowY: 'auto' }}>
							<table className='table table-sm mb-0' style={{ fontSize: 12 }}>
								<thead><tr className="table-header">
									<th style={{ paddingLeft: 14 }}>Head</th><th className='text-end'>Amount (৳)</th><th style={{ width: 120 }}>Share</th>
								</tr></thead>
								<tbody>
									{hc.map((r, i) => {
										const cost = toNum(r?.totalCost ?? r?.total ?? r?.amount ?? r?.cost ?? r?.expenseAmount);
										const share = expTotal > 0 ? (cost / expTotal) * 100 : 0;
										return (
											<tr key={`h-${i}`}>
												<td style={{ paddingLeft: 14 }}>{r?.head_name || r?.headName || '-'}</td>
												<td className='text-end fw-bold'>{cost.toLocaleString()}</td>
												<td><div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
													<div className="bar-track" style={{ height: 5, width: '100%' }}>
														<div className="bar-fill" style={{ width: `${Math.min(share, 100)}%`, background: '#94a3b8' }} />
													</div>
													<span style={{ fontSize: 10, color: '#94a3b8', minWidth: 30 }}>{share.toFixed(0)}%</span>
												</div></td>
											</tr>
										);
									})}
									{!loading && hc.length === 0 && <tr><td colSpan={3} className='text-muted text-center py-3'>No data</td></tr>}
								</tbody>
							</table>
						</div>
					</PanelCard>
				</div>
			</div>
		);
	};

	return (
		<>
			<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
				<SectionTitle icon="💰" title="Expense Overview" />
				<div style={{ display: 'flex', gap: 8 }}>
					<select className="form-select form-select-sm" style={{ width: 130 }} value={month} onChange={e => onMonthChange(e.target.value)}>
						<option value="">All Months</option>
						<option value="01">January</option><option value="02">February</option>
						<option value="03">March</option><option value="04">April</option>
						<option value="05">May</option><option value="06">June</option>
						<option value="07">July</option><option value="08">August</option>
						<option value="09">September</option><option value="10">October</option>
						<option value="11">November</option><option value="12">December</option>
					</select>
					<select className="form-select form-select-sm" style={{ width: 110 }} value={year} onChange={e => onYearChange(e.target.value)}>
						<option value="">All Years</option>
						{years.map(y => <option key={y} value={y}>{y}</option>)}
					</select>
				</div>
			</div>
			<div className='row g-4 mb-4'>
				{renderColumn("Light Vehicles", "🚗", "lightVehicle", activeLV)}
				{renderColumn("Motor Cycles", "🏍️", "motorCycle", activeMC)}
			</div>
		</>
	);
});

const FleetComposition = React.memo(({ extStats, loading }) => {
	const renderTypeComposition = (type) => {
		const typeStats = getTypeStats(extStats, type);
		const meta = VEHICLE_TYPE_META[type];
		const fuels = typeStats?.fuelTypes || extStats?.fuelTypes || [];
		const ages = typeStats?.ageDistribution || extStats?.ageDistribution || [];
		const brands = typeStats?.topBrands || extStats?.topBrands || [];
		const fuelMax = fuels.length > 0 ? Math.max(...fuels.map(x => toNum(x?.count))) : 1;
		const ageMax = ages.length > 0 ? Math.max(...ages.map(x => toNum(x?.count))) : 1;
		const brandMax = brands.length > 0 ? Math.max(...brands.map(x => toNum(x?.count))) : 1;
		const fuelColors = ['#0ea5e9', '#f59e0b', '#16a34a', '#8b5cf6', '#dc2626', '#d97706', '#64748b'];
		const ageColors = ['#16a34a', '#0ea5e9', '#f59e0b', '#d97706', '#dc2626'];

		return (
			<div className='col-12 col-xl-6'>
				<div className='panel-card' style={{ borderTop: `3px solid ${meta.color}`, background: meta.bg }}>
					<div className='panel-card-header'>
						<span className='panel-card-title'>{meta.icon} {meta.title}</span>
					</div>
					<div className='panel-card-body'>
						<div className='row g-3'>
							<div className='col-md-4'>
								<PanelCard title="⛽ Fuel Type">
									<div className="panel-card-padding">
										{fuels.map((f, i) => (
											<BarRow key={`${type}-fuel-${i}`} label={f?.fuelType || 'Unknown'} value={toNum(f?.count)} max={fuelMax} color={fuelColors[i % fuelColors.length]} />
										))}
										{!loading && fuels.length === 0 && <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: 12 }}>No data</div>}
									</div>
								</PanelCard>
							</div>
							<div className='col-md-4'>
								<PanelCard title="📅 Vehicle Age">
									<div className="panel-card-padding">
										{ages.map((a, i) => (
											<BarRow key={`${type}-age-${i}`} label={a?.ageGroup} value={toNum(a?.count)} max={ageMax} color={ageColors[i % ageColors.length]} />
										))}
										{!loading && ages.length === 0 && <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: 12 }}>No data</div>}
									</div>
								</PanelCard>
							</div>
							<div className='col-md-4'>
								<PanelCard title="🏷️ Top Brands">
									<div className="panel-card-padding">
										{brands.map((b, i) => (
											<BarRow key={`${type}-brand-${i}`} label={b?.brandName || 'Unknown'} value={toNum(b?.count)} max={brandMax} color="#8b5cf6" />
										))}
										{!loading && brands.length === 0 && <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: 12 }}>No data</div>}
									</div>
								</PanelCard>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	};

	return (
		<>
			<SectionTitle icon="🔍" title="Fleet Composition" />
			<div className='row g-3 mb-4'>
				{renderTypeComposition('lightVehicle')}
				{renderTypeComposition('motorCycle')}
			</div>
		</>
	);
});

const ProjectsVendors = React.memo(({ projectVehicle, categoryVehicle, extStats, loading }) => {
	const lightVendors = getTypeStats(extStats, 'lightVehicle')?.vendorWise || extStats?.vendorWise || [];
	const motorVendors = getTypeStats(extStats, 'motorCycle')?.vendorWise || [];
	const renderVendorRows = (vendors, color, prefix) => {
		const vendorTotal = vendors.reduce((s, x) => s + toNum(x?.count), 0) || 1;
		return vendors.map((v, i) => {
			const share = (toNum(v?.count) / vendorTotal) * 100;
			return (
				<tr key={`${prefix}-${i}`}>
					<td style={{ paddingLeft: 14 }}>{v?.vendorName || 'Unknown'}</td>
					<td className='text-center fw-bold'>{toNum(v?.count)}</td>
					<td><div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
						<div className="bar-track" style={{ height: 5, width: '100%' }}>
							<div className="bar-fill" style={{ width: `${Math.min(share, 100)}%`, background: color }} />
						</div>
						<span style={{ fontSize: 10, color: '#94a3b8', minWidth: 28 }}>{share.toFixed(0)}%</span>
					</div></td>
				</tr>
			);
		});
	};

	return (
		<>
			<SectionTitle icon="🏗️" title="Projects & Vendors" />
			<div className='row g-3 mb-4'>
				<div className='col-lg-7'>
					<PanelCard title="📁 Project Wise Vehicles" linkHref="/report-buWiseLightVehicle" linkLabel="Report →">
						<div style={{ maxHeight: 300, overflowY: 'auto' }}>
							<table className='table table-sm mb-0' style={{ fontSize: 12 }}>
								<thead>
									<tr className="table-header">
										<th rowSpan={2} style={{ paddingLeft: 14, verticalAlign: 'middle' }}>Project</th>
										<th colSpan={2} className='text-center' style={{ borderBottom: '1px solid #e2e8f0', color: '#0369a1' }}>Light Vehicle</th>
										<th colSpan={2} className='text-center' style={{ borderBottom: '1px solid #e2e8f0', color: '#b45309' }}>Motor Cycle</th>
										<th rowSpan={2} className='text-center' style={{ verticalAlign: 'middle' }}>Total</th>
									</tr>
									<tr className="table-header">
										<th className='text-center' style={{ color: '#166534', fontSize: 10 }}>Active</th>
										<th className='text-center' style={{ color: '#64748b', fontSize: 10 }}>Inactive</th>
										<th className='text-center' style={{ color: '#166534', fontSize: 10 }}>Active</th>
										<th className='text-center' style={{ color: '#64748b', fontSize: 10 }}>Inactive</th>
									</tr>
								</thead>
								<tbody>
									{(projectVehicle || []).map((r, i) => {
										const lvAct = toNum(r?.lightVehicleActive);
										const lvInact = toNum(r?.lightVehicleInactive);
										const mcAct = toNum(r?.motorCycleActive);
										const mcInact = toNum(r?.motorCycleInactive);
										const tot = toNum(r?.totalVehicleCount) || (lvAct + lvInact + mcAct + mcInact);
										return (
											<tr key={`p-${i}`}>
												<td style={{ paddingLeft: 14 }}>{r?.projectName || r?.project_name || '-'}</td>
												<td className='text-center'>{lvAct}</td>
												<td className='text-center'>{lvInact}</td>
												<td className='text-center'>{mcAct}</td>
												<td className='text-center'>{mcInact}</td>
												<td className='text-center fw-bold'>{tot}</td>
											</tr>
										);
									})}
									{!loading && (!projectVehicle || projectVehicle.length === 0) && <tr><td colSpan={6} className='text-muted text-center py-3'>No data</td></tr>}
								</tbody>
							</table>
						</div>
					</PanelCard>
				</div>
				<div className='col-lg-5'>
					<div className='d-flex flex-column gap-3'>
						<PanelCard title="🤝 Vendor-wise Third Party (Light Vehicle)">
							<div style={{ maxHeight: 145, overflowY: 'auto' }}>
								<table className='table table-sm mb-0' style={{ fontSize: 12 }}>
									<thead><tr className="table-header">
										<th style={{ paddingLeft: 14 }}>Vendor</th><th className='text-center'>Vehicles</th><th style={{ width: 90 }}>Share</th>
									</tr></thead>
									<tbody>
										{renderVendorRows(lightVendors, '#6d28d9', 'lv')}
										{!loading && lightVendors.length === 0 && <tr><td colSpan={3} className='text-muted text-center py-3'>No data</td></tr>}
									</tbody>
								</table>
							</div>
						</PanelCard>
						<PanelCard title="🤝 Vendor-wise Third Party (Motor Cycle)">
							<div style={{ maxHeight: 145, overflowY: 'auto' }}>
								<table className='table table-sm mb-0' style={{ fontSize: 12 }}>
									<thead><tr className="table-header">
										<th style={{ paddingLeft: 14 }}>Vendor</th><th className='text-center'>Vehicles</th><th style={{ width: 90 }}>Share</th>
									</tr></thead>
									<tbody>
										{renderVendorRows(motorVendors, '#c2410c', 'mc')}
										{!loading && motorVendors.length === 0 && <tr><td colSpan={3} className='text-muted text-center py-3'>No data</td></tr>}
									</tbody>
								</table>
							</div>
						</PanelCard>
					</div>
				</div>
			</div>
			<div className='row g-3'>
				<div className='col-12'>
					<PanelCard title="📂 Category Wise Vehicles" linkHref="/report-typeWiseVehicle" linkLabel="Report →">
						<div style={{ maxHeight: 300, overflowY: 'auto' }}>
							<table className='table table-sm mb-0' style={{ fontSize: 12 }}>
								<thead>
									<tr className="table-header">
										<th rowSpan={2} style={{ paddingLeft: 14, verticalAlign: 'middle' }}>Category</th>
										<th colSpan={2} className='text-center' style={{ borderBottom: '1px solid #e2e8f0', color: '#0369a1' }}>Light Vehicle</th>
										<th colSpan={2} className='text-center' style={{ borderBottom: '1px solid #e2e8f0', color: '#b45309' }}>Motor Cycle</th>
										<th rowSpan={2} className='text-center' style={{ verticalAlign: 'middle' }}>Total</th>
									</tr>
									<tr className="table-header">
										<th className='text-center' style={{ color: '#166534', fontSize: 10 }}>Active</th>
										<th className='text-center' style={{ color: '#64748b', fontSize: 10 }}>Inactive</th>
										<th className='text-center' style={{ color: '#166534', fontSize: 10 }}>Active</th>
										<th className='text-center' style={{ color: '#64748b', fontSize: 10 }}>Inactive</th>
									</tr>
								</thead>
								<tbody>
									{(categoryVehicle || []).map((r, i) => {
										const lvAct = toNum(r?.lightVehicleActive);
										const lvInact = toNum(r?.lightVehicleInactive);
										const mcAct = toNum(r?.motorCycleActive);
										const mcInact = toNum(r?.motorCycleInactive);
										const t = toNum(r?.totalVehicle) || (lvAct + lvInact + mcAct + mcInact);
										return (
											<tr key={`c-${i}`}>
												<td style={{ paddingLeft: 14 }}>{r?.categoryName || r?.category_name || 'Others'}</td>
												<td className='text-center'>{lvAct}</td>
												<td className='text-center'>{lvInact}</td>
												<td className='text-center'>{mcAct}</td>
												<td className='text-center'>{mcInact}</td>
												<td className='text-center fw-bold'>{t}</td>
											</tr>
										);
									})}
									{!loading && (!categoryVehicle || categoryVehicle.length === 0) && <tr><td colSpan={6} className='text-muted text-center py-3'>No data</td></tr>}
								</tbody>
							</table>
						</div>
					</PanelCard>
				</div>
			</div>
		</>
	);
});


/* ── Main Data Controller Component ── */
function ManagementDashboard() {
	const { jwt, businessUnit } = useContext(userPermissionContext);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	
	// State
	const [fleet, setFleet] = useState({});
	const [expiryExpired, setExpiryExpired] = useState({});
	const [expirySoon, setExpirySoon] = useState({});
	const [expenseSummary, setExpenseSummary] = useState({});
	const [projectVehicle, setProjectVehicle] = useState([]);
	const [categoryVehicle, setCategoryVehicle] = useState([]);
	const [extStats, setExtStats] = useState({});
	const [lastSync, setLastSync] = useState(null);

	// Expense Filter State
	const currentDate = new Date();
	const currentMonth = String(currentDate.getMonth() + 1).padStart(2, '0');
	const currentYearStr = String(currentDate.getFullYear());
	const [expenseMonth, setExpenseMonth] = useState(currentMonth);
	const [expenseYear, setExpenseYear] = useState(currentYearStr);

	useEffect(() => {
		let cancelled = false;
		const token = jwt?.token;
		if (!token) return;
		const headers = { Authorization: `Bearer ${token}` };
		const pid = businessUnit?.idProjects;
		let qp = pid != null && pid !== '' ? `?currentProject=${encodeURIComponent(pid)}&projectId=${encodeURIComponent(pid)}` : '?';
		if (expenseMonth) qp += `${qp.length > 1 ? '&' : ''}expenseMonth=${encodeURIComponent(expenseMonth)}`;
		if (expenseYear) qp += `${qp.length > 1 ? '&' : ''}expenseYear=${encodeURIComponent(expenseYear)}`;
		const cacheKey = buildDashboardCacheKey(pid, expenseMonth, expenseYear);
		const cached = readDashboardCache(cacheKey);
		const setters = {
			setFleet,
			setExpiryExpired,
			setExpirySoon,
			setExpenseSummary,
			setProjectVehicle,
			setCategoryVehicle,
			setExtStats,
			setLastSync,
		};

		if (cached) {
			applyDashboardData(cached, setters);
			setLoading(false);
		}

		const controller = new AbortController();
		const get = (url) => fetch(url, { headers, signal: controller.signal })
			.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)));

		(async () => {
			try {
				if (!cached) {
					setLoading(true);
				}
				setError('');
				const res = await get(`${API_URL}vms_app_dashboard_summary${qp}`);
				
				if (cancelled) return;
				const d = res.data || {};
				applyDashboardData(d, setters);
				writeDashboardCache(cacheKey, d);
			} catch (err) {
				if (!cancelled && err?.name !== 'AbortError' && !cached) {
					setError('Failed to load dashboard.');
				}
			}
			finally { if (!cancelled) setLoading(false); }
		})();
		return () => {
			cancelled = true;
			controller.abort();
		};
	}, [jwt?.token, businessUnit?.idProjects, expenseMonth, expenseYear]);

	return (
		<div className="dashboard-container">
			<div className="dashboard-header">
				<div className="dashboard-header-title">
					<KTSVG path='/media/svg/icons/Layout/Layout-4-blocks.svg' className='svg-icon-1 svg-icon-primary' />
					<span className="dashboard-header-text">Fleet Management Dashboard</span>
					{businessUnit?.projectName && <span className="dashboard-header-project">— {businessUnit.projectName}</span>}
				</div>
				<span className="dashboard-header-time">{lastSync ? `Synced ${lastSync.toLocaleTimeString()}` : 'Loading...'}</span>
			</div>

			<div className="dashboard-body">
				{error && <div className='alert alert-warning py-2 mb-3' style={{ fontSize: 12 }}>{error}</div>}

				<FleetOverview fleet={fleet} loading={loading} />
				<OperationsDriver extStats={extStats} loading={loading} />
				<AlertsCompliance extStats={extStats} expiryExpired={expiryExpired} expirySoon={expirySoon} loading={loading} />
				<ExpenseOverview 
					expenseSummary={expenseSummary} 
					extStats={extStats} 
					activeLV={toNum(fleet.activeLightVehicle)} 
					activeMC={toNum(fleet.activeMotorCycle)} 
					loading={loading}
					month={expenseMonth}
					year={expenseYear}
					onMonthChange={setExpenseMonth}
					onYearChange={setExpenseYear}
				/>
				<FleetComposition extStats={extStats} loading={loading} />
				<ProjectsVendors projectVehicle={projectVehicle} categoryVehicle={categoryVehicle} extStats={extStats} loading={loading} />
			</div>
		</div>
	);
}

export default ManagementDashboard;
